import {
  CampaignStatusSchema,
  CampaignType,
  findUnknownMacros,
  POP_DESTINATION_MAX,
} from "@adlywise/shared";
import { toDateInputValue } from "@/utils/dateUtils";

const Status = CampaignStatusSchema.enum;

export const STATUSES = [
  { value: Status.draft, label: "Draft" },
  { value: Status.active, label: "Active" },
  { value: Status.paused, label: "Paused" },
  { value: Status.completed, label: "Completed" },
];

export const DEFAULT_TRANSIT = {
  entrypoint: "",
  // Our commission (% of the publisher floor) before the 50/50 profit split.
  margin: 0,
  qps: 1,
  // Blank ⇒ the platform default response budget is used.
  timeoutMs: "",
  // Routing filters for this DSP endpoint (see EndpointFilters); null ⇒ none.
  filters: null,
  selectedPublishers: [],
  blockList: [],
  whiteList: [],
  // Server-minted public statistics token; read-only here, carried through a
  // save so editing a campaign never drops its partner link.
  statsToken: "",
};

// Popunder config as edited in the wizard (strings for the inputs). Audience
// targeting (country, region, …) is the campaign's segments, not part of it.
export const DEFAULT_POP = {
  destination: "",
  selectedPublishers: [],
  capCount: "1",
  capWindowHours: "24",
  marginPct: "15",
  includedZones: "",
  includedSiteDomains: "",
  excludedZones: "",
  excludedSiteDomains: "",
  adomain: "",
  costMacro: "sell",
  confirmMode: "fingerprint",
  cpaTarget: "fdp",
  // Server-minted; shown for the tracker setup, never sent back.
  postbackToken: "",
};

const splitList = (text) =>
  String(text ?? "")
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);

export const popFormFromMetadata = (meta) => {
  if (!meta || typeof meta !== "object" || !("destination" in meta)) {
    return { ...DEFAULT_POP };
  }
  return {
    destination: meta.destination ?? "",
    selectedPublishers: Array.isArray(meta.selectedPublishers)
      ? meta.selectedPublishers
      : [],
    // Read-only: country/region targeting saved before it moved to segments.
    // Such a campaign does not bid until an admin converts it.
    legacyGeo: [
      meta.geo?.include,
      meta.geo?.exclude,
      meta.geo?.excludeRegions,
    ].some((l) => Array.isArray(l) && l.length > 0)
      ? meta.geo
      : null,
    capCount: String(meta.cap?.count ?? 1),
    capWindowHours: String(
      Math.round((meta.cap?.windowSeconds ?? 86400) / 3600),
    ),
    // The stored window, so an unedited non-hour value survives a save.
    capWindowSeconds: meta.cap?.windowSeconds ?? 86400,
    marginPct: String(meta.marginPct ?? 15),
    includedZones: (meta.includedZones ?? []).join("\n"),
    includedSiteDomains: (meta.includedSiteDomains ?? []).join("\n"),
    excludedZones: (meta.excludedZones ?? []).join("\n"),
    excludedSiteDomains: (meta.excludedSiteDomains ?? []).join("\n"),
    adomain: meta.adomain ?? "",
    costMacro: meta.costMacro ?? "sell",
    confirmMode: meta.confirmMode ?? "fingerprint",
    cpaTarget: meta.cpaTarget ?? "fdp",
    postbackToken: meta.postbackToken ?? "",
  };
};

// The API metadata for a popunder campaign (the server validates, fills
// defaults, normalises countries and keeps its own postback token).
export const toPopMetadata = (pop) => ({
  destination: pop.destination.trim(),
  selectedPublishers: pop.selectedPublishers,
  cap: {
    count: Number(pop.capCount),
    windowSeconds:
      pop.capWindowSeconds != null &&
      String(Math.round(pop.capWindowSeconds / 3600)) ===
        String(pop.capWindowHours)
        ? pop.capWindowSeconds
        : Math.round(Number(pop.capWindowHours) * 3600),
  },
  marginPct: Number(pop.marginPct),
  includedZones: splitList(pop.includedZones),
  includedSiteDomains: splitList(pop.includedSiteDomains).map((d) =>
    d.toLowerCase(),
  ),
  excludedZones: splitList(pop.excludedZones),
  excludedSiteDomains: splitList(pop.excludedSiteDomains).map((d) =>
    d.toLowerCase(),
  ),
  ...(pop.adomain.trim() ? { adomain: pop.adomain.trim().toLowerCase() } : {}),
  costMacro: pop.costMacro,
  confirmMode: pop.confirmMode,
  cpaTarget: pop.cpaTarget,
});

const DESTINATION_RE = /^https?:\/\/[\x21\x23-\x5b\x5d-\x7e]+$/;

// What the API would refuse, in words; empty ⇒ the step is complete.
export const popFormErrors = (pop, cpm) => {
  const errors = [];
  const dest = pop.destination.trim();
  if (!dest) errors.push("Enter the destination (tracker) URL.");
  else if (dest.length > POP_DESTINATION_MAX)
    errors.push(
      `The destination is longer than ${POP_DESTINATION_MAX} characters.`,
    );
  else if (!DESTINATION_RE.test(dest))
    errors.push(
      "The destination must be an http(s) URL of plain ASCII, without spaces, quotes or backslashes.",
    );
  else {
    const unknown = findUnknownMacros(dest);
    if (unknown.length > 0)
      errors.push(`Unknown macro: ${unknown.map((m) => `{${m}}`).join(", ")}`);
  }
  if (pop.selectedPublishers.length === 0)
    errors.push("Select at least one popunder publisher.");
  if (!(Number(cpm) > 0)) errors.push("Set a CPM above 0.");
  const margin = Number(pop.marginPct);
  if (!Number.isInteger(margin) || margin < 1 || margin > 90)
    errors.push("Margin must be a whole number from 1 to 90.");
  const count = Number(pop.capCount);
  if (!Number.isInteger(count) || count < 1 || count > 10)
    errors.push("Frequency cap must be 1–10 visits.");
  const bad = (label, values, re) => {
    const wrong = values.filter((v) => !re.test(v));
    if (wrong.length > 0) errors.push(`${label}: ${wrong.join(", ")}`);
  };
  bad(
    "Not a zone id",
    [...splitList(pop.includedZones), ...splitList(pop.excludedZones)],
    /^[A-Za-z0-9._:-]{1,64}$/,
  );
  const both = (a, b) => {
    const set = new Set(splitList(b).map((v) => v.toLowerCase()));
    return splitList(a).filter((v) => set.has(v.toLowerCase()));
  };
  const clash = [
    ...both(pop.includedZones, pop.excludedZones),
    ...both(pop.includedSiteDomains, pop.excludedSiteDomains),
  ];
  if (clash.length > 0)
    errors.push(`Both allowed and blocked: ${clash.join(", ")}`);
  bad(
    "Not a domain",
    [
      ...splitList(pop.includedSiteDomains),
      ...splitList(pop.excludedSiteDomains),
      ...(pop.adomain.trim() ? [pop.adomain.trim()] : []),
    ].map((d) => d.toLowerCase()),
    /^[a-z0-9.-]{1,253}$/,
  );
  const hours = Number(pop.capWindowHours);
  if (!Number.isInteger(hours) || hours < 1 || hours > 168)
    errors.push("Cap window must be 1–168 hours.");
  return errors;
};

export const DEFAULT_CAMPAIGN = {
  name: "",
  description: "",
  status: Status.draft,
  type: CampaignType.Standard,
  selectedSegments: [],
  userId: "",
  defaultCreativeId: "",
  startDate: "",
  endDate: "",
  icon: null,
  cpm: 0,
  dailyFrequency: "",
  weeklyFrequency: "",
  impressionsCap: "",
  budgetCap: "",
  cooldownSeconds: "",
  // creativeUuid -> cooldown seconds (kept as input-friendly strings in the form)
  creativeCooldowns: {},
  impressionDomain: null,
  metadata: { ...DEFAULT_TRANSIT },
  pop: { ...DEFAULT_POP },
};

export const toFormState = (campaign) => {
  if (!campaign)
    return {
      ...DEFAULT_CAMPAIGN,
      metadata: { ...DEFAULT_TRANSIT },
      pop: { ...DEFAULT_POP },
    };

  // Read metadata for both types, not transit-only: standard campaigns now also
  // persist `selectedPublishers` here, so editing must hydrate it to survive a
  // round-trip.
  const rawMeta =
    campaign.metadata && typeof campaign.metadata === "object"
      ? campaign.metadata
      : {};
  const isTransit = campaign.type === CampaignType.Transit;
  const isPop = campaign.type === CampaignType.Popunder;
  return {
    ...DEFAULT_CAMPAIGN,
    ...campaign,
    type: isTransit
      ? CampaignType.Transit
      : isPop
        ? CampaignType.Popunder
        : CampaignType.Standard,
    pop: isPop ? popFormFromMetadata(rawMeta) : { ...DEFAULT_POP },
    selectedSegments: Array.isArray(campaign.selectedSegments)
      ? campaign.selectedSegments
          .map(String)
          .filter((id) => id && id !== "undefined" && id !== "null")
      : [],
    defaultCreativeId: campaign.defaultCreativeId ?? "",
    startDate: toDateInputValue(campaign.startDate),
    endDate: toDateInputValue(campaign.endDate),
    cpm: campaign.cpm ?? 0,
    dailyFrequency:
      campaign.dailyFrequency != null ? String(campaign.dailyFrequency) : "",
    weeklyFrequency:
      campaign.weeklyFrequency != null ? String(campaign.weeklyFrequency) : "",
    impressionsCap:
      campaign.impressionsCap != null ? String(campaign.impressionsCap) : "",
    budgetCap: campaign.budgetCap != null ? String(campaign.budgetCap) : "",
    cooldownSeconds:
      campaign.cooldownSeconds != null ? String(campaign.cooldownSeconds) : "",
    creativeCooldowns:
      campaign.creativeCooldowns &&
      typeof campaign.creativeCooldowns === "object"
        ? Object.fromEntries(
            Object.entries(campaign.creativeCooldowns).map(([id, secs]) => [
              id,
              String(secs),
            ]),
          )
        : {},
    impressionDomain: campaign.impressionDomain ?? null,
    metadata: {
      entrypoint: isTransit ? (rawMeta.entrypoint ?? "") : "",
      margin: isTransit ? (rawMeta.margin ?? 0) : 0,
      qps: isTransit ? (rawMeta.qps ?? 1) : 1,
      timeoutMs:
        isTransit && rawMeta.timeoutMs != null ? String(rawMeta.timeoutMs) : "",
      filters: isTransit ? (rawMeta.filters ?? null) : null,
      statsToken: isTransit ? (rawMeta.statsToken ?? "") : "",
      selectedPublishers: Array.isArray(rawMeta.selectedPublishers)
        ? rawMeta.selectedPublishers
        : [],
      blockList: isTransit
        ? Array.isArray(rawMeta.blockList)
          ? rawMeta.blockList
          : []
        : [],
      whiteList: isTransit
        ? Array.isArray(rawMeta.whiteList)
          ? rawMeta.whiteList
          : []
        : [],
    },
  };
};

export const toNullableInt = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
};

export const toNullableFloat = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// Build the API `creativeCooldowns` map: keep only positive-integer seconds and
// only for creatives currently assigned to the campaign (drops stale entries for
// creatives that were unassigned). Returns {} when nothing is set.
export const round2 = (n) =>
  Math.round((Number(n) + Number.EPSILON) * 100) / 100;

export const maxProviderCost = (selectedPublishers, providers) => {
  const byUuid = new Map(
    (Array.isArray(providers) ? providers : []).map((p) => [
      p.uuid,
      Number(p.cpm) || 0,
    ]),
  );
  return (Array.isArray(selectedPublishers) ? selectedPublishers : []).reduce(
    (max, uuid) => Math.max(max, byUuid.get(uuid) ?? 0),
    0,
  );
};

// Minimum allowed campaign CPM for a standard campaign: strictly above the
// priciest selected provider's cost (serving requires campaign cpm > provider
// cost). Returns 0 when no providers are selected.
export const minCampaignCpm = (selectedPublishers, providers) => {
  if (!(Array.isArray(selectedPublishers) && selectedPublishers.length > 0))
    return 0;
  return round2(maxProviderCost(selectedPublishers, providers) + 0.01);
};

export const toCreativeCooldownsPayload = (map, assignedIds) => {
  const allowed = new Set((assignedIds ?? []).filter(Boolean));
  const out = {};
  for (const [id, value] of Object.entries(map ?? {})) {
    if (!allowed.has(id)) continue;
    const n = parseInt(value, 10);
    if (Number.isFinite(n) && n > 0) out[id] = n;
  }
  return out;
};

// The API payload for a popunder campaign (create or update). Only fields the
// popunder contract uses; no segments, creatives or VAST frequency fields.
export const toPopCampaignPayload = (form, dates) => ({
  name: form.name,
  description: form.description,
  icon: form.icon,
  startDate: dates.startDate,
  endDate: dates.endDate,
  type: CampaignType.Popunder,
  selectedSegments: form.selectedSegments ?? [],
  defaultCreativeId: null,
  cpm: Number(form.cpm) || 0,
  impressionsCap: toNullableInt(form.impressionsCap),
  budgetCap: toNullableFloat(form.budgetCap),
  metadata: toPopMetadata(form.pop),
});
